import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  Badge,
  Popover,
  Typography,
  List,
  ListItem,
  Divider,
  Button,
  Chip,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Close as CloseIcon,
  RateReview as RateReviewIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import notificationsAPI from '../services/notificationsAPI';

interface NotificationMetadata {
  category?: string;
  grant_id?: string;
  agent_name?: string;
  agent_code?: string;
  target_application_id?: string;
  target_application_name?: string;
  review_stage?: 'owner' | 'security';
  grant_status?: string;
}

interface Notification {
  id: string;
  user_id: string;
  from_user_id?: string;
  from_username?: string;
  first_name?: string;
  last_name?: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'status_changed' | 'comment_added' | 'approval_required';
  title: string;
  message: string;
  related_entity_type?: string;
  related_entity_id?: string;
  is_read: boolean;
  created_at: string;
  read_at?: string;
  action_url?: string;
  metadata?: NotificationMetadata | string;
}

function parseMetadata(raw: Notification['metadata']): NotificationMetadata | null {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as NotificationMetadata;
    } catch {
      return null;
    }
  }
  return raw;
}

function isAiAccessNotification(notification: Notification): boolean {
  const meta = parseMetadata(notification.metadata);
  return (
    notification.related_entity_type === 'ai_access_grant' ||
    meta?.category?.startsWith('ai_') === true
  );
}

function getReviewLabel(notification: Notification): string {
  const meta = parseMetadata(notification.metadata);
  if (meta?.review_stage === 'security') return 'Review Security Request';
  if (notification.type === 'approval_required') return 'Review Request';
  return 'View Details';
}

const NotificationCenter: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (user) {
      loadNotifications();
      loadUnreadCount();
    }
  }, [user]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationsAPI.getNotifications(20, 0);
      if (response.success) {
        setNotifications(response.data.notifications);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await notificationsAPI.getUnreadCount();
      if (response.success) {
        setUnreadCount(response.data.unreadCount);
      }
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    loadNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === notificationId
            ? { ...notif, is_read: true, read_at: new Date().toISOString() }
            : notif
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      showError('Failed to mark notification as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications((prev) =>
        prev.map((notif) => ({ ...notif, is_read: true, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
      showSuccess('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      showError('Failed to mark all notifications as read');
    }
  };

  const handleDelete = async (notificationId: string, wasUnread: boolean) => {
    try {
      await notificationsAPI.deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((notif) => notif.id !== notificationId));
      if (wasUnread) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      showError('Failed to delete notification');
    }
  };

  const handleReview = async (notification: Notification) => {
    if (!notification.is_read) {
      await handleMarkAsRead(notification.id);
    }
    handleClose();
    const url = notification.action_url || '/ai-governance';
    navigate(url);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
      case 'status_changed':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'warning':
      case 'approval_required':
        return <WarningIcon color="warning" fontSize="small" />;
      case 'error':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'comment_added':
        return <InfoIcon color="info" fontSize="small" />;
      default:
        return <InfoIcon color="action" fontSize="small" />;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const renderNotificationBody = (notification: Notification) => {
    const meta = parseMetadata(notification.metadata);

    if (meta?.agent_name && meta?.target_application_name) {
      const reviewHint =
        meta.review_stage === 'security'
          ? 'Security reviewer approval is required.'
          : notification.type === 'approval_required'
            ? 'Your approval is required as the application owner.'
            : null;

      return (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.5, wordBreak: 'break-word' }}
        >
          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {meta.agent_name}
          </Box>
          {meta.agent_code && (
            <Typography
              component="span"
              variant="caption"
              sx={{ ml: 0.5, color: 'text.disabled', fontFamily: 'monospace' }}
            >
              ({meta.agent_code})
            </Typography>
          )}
          {' is requesting access to '}
          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {meta.target_application_name}
          </Box>
          {reviewHint ? `. ${reviewHint}` : '.'}
        </Typography>
      );
    }

    return (
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, wordBreak: 'break-word' }}>
        {notification.message}
      </Typography>
    );
  };

  const showReviewAction = (notification: Notification) =>
    notification.type === 'approval_required' ||
    (isAiAccessNotification(notification) && Boolean(notification.action_url));

  const notificationGroups = notifications.reduce<Notification[][]>((groups, notification) => {
    const key = `${notification.type}\u0001${notification.title}\u0001${notification.message}`;
    const existing = groups.find((group) => `${group[0].type}\u0001${group[0].title}\u0001${group[0].message}` === key);
    if (existing) existing.push(notification);
    else groups.push([notification]);
    return groups;
  }, []);

  const openNotification = async (group: Notification[]) => {
    const head = group[0];
    await Promise.all(group.filter((item) => !item.is_read).map((item) => handleMarkAsRead(item.id)));
    handleClose();
    navigate(head.action_url || (isAiAccessNotification(head) ? '/ai-governance' : '/dashboard'));
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton onClick={handleClick} sx={{ color: 'inherit' }}>
          <Badge badgeContent={unreadCount} color="error">
            {unreadCount > 0 ? <NotificationsActiveIcon /> : <NotificationsIcon />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: { xs: 'min(100vw - 24px, 440px)', sm: 440 },
            maxHeight: 640,
            mt: 1,
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: unreadCount > 0 ? 0.5 : 0 }}>
            <Typography variant="h6" fontWeight="bold">
              Notifications
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {unreadCount > 0 && (
                <Button
                  size="small"
                  startIcon={<MarkEmailReadIcon sx={{ fontSize: '1rem !important' }} />}
                  onClick={handleMarkAllAsRead}
                  sx={{ whiteSpace: 'nowrap', minWidth: 0, px: 1 }}
                >
                  Mark all read
                </Button>
              )}
              <IconButton size="small" onClick={handleClose} aria-label="Close notifications">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          {unreadCount > 0 && (
            <Typography variant="body2" color="text.secondary">
              {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        <Box sx={{ maxHeight: 520, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No notifications yet
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {notificationGroups.map((group, index) => {
                const notification = group[0];
                return (
                <React.Fragment key={notification.id}>
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      display: 'block',
                      py: 1.75,
                      px: 2,
                      backgroundColor: notification.is_read ? 'inherit' : 'action.hover',
                      '&:hover': { backgroundColor: 'action.selected' },
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                      <Box sx={{ pt: 0.25, flexShrink: 0 }}>{getNotificationIcon(notification.type)}</Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mb: 0.75 }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={notification.is_read ? 500 : 700}
                            sx={{ lineHeight: 1.3 }}
                          >
                            {notification.title}
                          </Typography>
                          {group.length > 1 && (
                            <Chip
                              label={`${group.length} similar`}
                              size="small"
                              sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                            />
                          )}
                          {!notification.is_read && (
                            <Chip
                              label="New"
                              size="small"
                              color="primary"
                              sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                            />
                          )}
                        </Box>

                        {renderNotificationBody(notification)}

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                          {formatTimeAgo(notification.created_at)}
                          {notification.from_username && ` · by ${notification.from_username}`}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mt: 1.25,
                        ml: 3.75,
                      }}
                    >
                      {showReviewAction(notification) ? (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<RateReviewIcon sx={{ fontSize: '1rem !important' }} />}
                          onClick={() => void openNotification(group)}
                          sx={{ textTransform: 'none', fontWeight: 600, py: 0.5 }}
                        >
                          {getReviewLabel(notification)}
                        </Button>
                      ) : notification.action_url ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => void openNotification(group)}
                          sx={{ textTransform: 'none' }}
                        >
                          View
                        </Button>
                      ) : (
                        <Box />
                      )}

                      <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                        {!notification.is_read && (
                          <Tooltip title="Mark as read">
                            <IconButton
                              size="small"
                              aria-label="Mark as read"
                              onClick={() => void Promise.all(group.filter((item) => !item.is_read).map((item) => handleMarkAsRead(item.id)))}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            aria-label="Delete notification"
                            onClick={() => void handleDelete(notification.id, !notification.is_read)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </ListItem>
                  {index < notificationGroups.length - 1 && <Divider />}
                </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationCenter;
